import { action, computed, observable } from 'mobx'
import type { WorkspaceSearcher } from '@/features/iam/workspaces/WorkspaceSearcher'
import { SearchTextViewModel } from '@/infra/SearchTextViewModel'
import { type InventoryStore } from '@/features/inventory/InventoryStore'

/**
 * Searches the workspaces that have inventory items.
 */
export class WorkspaceSearchViewModel implements WorkspaceSearcher {
  /**
   * User is searching a workspace
   */
  @observable accessor editing = false

  private readonly _searchText = new SearchTextViewModel()

  constructor(private readonly _inventoryStore: InventoryStore) {}

  @action.bound
  startEditing() {
    this.editing = true
  }

  @action.bound
  stopEditing() {
    this.editing = false
  }

  /**
   * Fetches the workspaces that have inventory items.
   *
   * @param signal
   */
  async refreshWorkspacesWithInventoryItems(signal?: AbortSignal) {
    await this._inventoryStore.fetchWorkspacesWithInventoryItems(signal)
  }

  /**
   * The search text, without waiting for the debounce.
   */
  @computed
  get searchText() {
    return this._searchText.value
  }

  /**
   * Returns only active workspaces
   */
  @computed
  get activeWorkspaces() {
    return this._inventoryStore.workspacesWithInventoryItems.filter(
      // Workspaces show once they have loaded.
      (item) => item.workspace.name !== '',
    )
  }

  /**
   * Filtered workspaces
   */
  @computed
  get filteredWorkspaces() {
    const result = this.activeWorkspaces

    if (this.searchText) {
      const searchText = this.searchText.toLowerCase()

      return result.filter((item) =>
        item.workspace?.name.toLocaleLowerCase().includes(searchText),
      )
    }
    return result
  }

  /**
   * Sets the search text.
   *
   * @param searchText
   */
  @action.bound
  setSearchText(searchText: string) {
    this._searchText.change(searchText)
  }
}
